import { appConfig } from "@webcore/config/appConfig";
import { MfeDownloadMessage, mfeDownloadMessageType } from "@webcore/services/mfe/mfeMessaging";

/**
 * The cookie-authenticated API route that streams an ops app document as an attachment. The
 * same route serves every host: the main app calls it from the top-level window, the embedded
 * ops MFE hands it to its parent (see requestOpsAppDocumentDownload).
 */
export function buildOpsAppDocumentDownloadUrl(documentId: string): string {
  return `${appConfig.apiBaseUrl}/v1/teams/active/ops-app-documents/${encodeURIComponent(documentId)}/download`;
}

/**
 * Starts a browser download of a cookie-authenticated URL through a transient anchor, the same
 * way the app's other file downloads (context files, data exports) hand a URL to the browser.
 */
export function triggerAnchorDownload(url: string): void {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "";
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

/**
 * Downloads an ops app document from wherever the platform is hosted. Inside an embedded frame
 * (the ops MFE, or a sandboxed custom-view frame where downloads may be blocked) the request is
 * posted to the parent window, which owns the full-scope session and triggers the download
 * outside the frame; at the top level the download is triggered directly.
 */
export function requestOpsAppDocumentDownload(documentId: string): void {
  if (window.parent !== window) {
    const message: MfeDownloadMessage = { type: mfeDownloadMessageType, documentId };
    window.parent.postMessage(message, "*");
    return;
  }

  triggerAnchorDownload(buildOpsAppDocumentDownloadUrl(documentId));
}
